import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { noteArticles, noteCategories } from '@/mocks/notes';

interface NotesSearchProps {
  onResultsChange?: (hasResults: boolean, query: string) => void;
}

export default function NotesSearch({ onResultsChange }: NotesSearchProps) {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return noteArticles.filter((article) => {
      const matchCategory = activeCategory === 'all' || article.category === activeCategory;
      if (!q) return matchCategory;
      const inTitle = article.title.toLowerCase().includes(q);
      const inExcerpt = article.excerpt.toLowerCase().includes(q);
      const inTags = article.tags.some((t) => t.toLowerCase().includes(q));
      const inIntro = article.content.intro.toLowerCase().includes(q);
      return matchCategory && (inTitle || inExcerpt || inTags || inIntro);
    });
  }, [query, activeCategory]);

  const hasActiveSearch = query.trim().length > 0;

  useEffect(() => {
    onResultsChange?.(hasActiveSearch, query.trim());
  }, [hasActiveSearch, query, onResultsChange]);

  const clearSearch = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  const highlightText = (text: string, q: string) => {
    if (!q.trim()) return text;
    const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-[#e8d8c0] text-[#5a3e22] rounded px-0.5 not-italic font-medium">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className="w-full">
      {/* Search Input */}
      <div className={`relative flex items-center gap-3 bg-white border-2 rounded-2xl px-5 py-4 transition-all duration-300 ${isFocused ? 'border-[#b08060]' : 'border-[#e0d4c0]'}`}>
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <i className="ri-search-line text-[#b08060] text-xl"></i>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="搜尋文章標題、主題、關鍵字……"
          className="flex-1 bg-transparent outline-none text-[1rem] text-[#3a2e22] placeholder-[#c0b4a0] text-sm"
        />
        {hasActiveSearch && (
          <button
            onClick={clearSearch}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#f0ebe2] hover:bg-[#e0d8cc] transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-[#7a6248] text-base"></i>
          </button>
        )}
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mt-4">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-4 py-1.5 rounded-full text-[0.875rem] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
            activeCategory === 'all'
              ? 'bg-[#5a3e22] text-white'
              : 'border border-[#d4c4b0] text-[#7a6248] hover:bg-[#f5f0e8]'
          }`}
        >
          全部
        </button>
        {noteCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-1.5 rounded-full text-[0.875rem] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeCategory === cat.id
                ? 'text-white'
                : 'border border-[#d4c4b0] text-[#7a6248] hover:bg-[#f5f0e8]'
            }`}
            style={activeCategory === cat.id ? { backgroundColor: cat.color } : {}}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Search Results */}
      {hasActiveSearch && (
        <div className="mt-8">
          {results.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#f0ebe2] mx-auto mb-4">
                <i className="ri-file-search-line text-[#b08060] text-2xl"></i>
              </div>
              <p className="text-[1rem] text-[#7a6e62] mb-1">
                找不到「<strong className="text-[#3a2e22]">{query}</strong>」相關文章
              </p>
              <p className="text-[0.9375rem] text-[#a89880]">試試其他關鍵字，或直接瀏覽下方分類</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-5">
                <p className="text-[0.9375rem] text-[#7a6e62]">
                  找到 <span className="text-[#5a3e22] font-semibold">{results.length}</span> 篇相關文章
                </p>
                <button
                  onClick={clearSearch}
                  className="text-[0.875rem] text-[#b08060] hover:text-[#7a6248] underline underline-offset-2 cursor-pointer whitespace-nowrap"
                >
                  清除搜尋
                </button>
              </div>
              <div className="space-y-3">
                {results.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/notes/${article.slug}`}
                    className="group flex gap-4 bg-white rounded-xl border border-[#ede5d8] hover:border-[#c8b89a] p-4 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className="text-[0.75rem] px-2 py-0.5 rounded-full text-white font-medium whitespace-nowrap"
                          style={{ backgroundColor: noteCategories.find((c) => c.id === article.category)?.color ?? '#b08060' }}
                        >
                          {article.categoryLabel}
                        </span>
                        <span className="text-[0.75rem] text-[#a89880]">約 {article.readTime} 分鐘</span>
                      </div>
                      <h4 className="font-serif-tc text-[#2c2420] text-[0.9375rem] leading-[1.4] mb-1 group-hover:text-[#7a6248] transition-colors line-clamp-1">
                        {highlightText(article.title, query.trim())}
                      </h4>
                      <p className="text-[0.8125rem] text-[#7a6e62] leading-[1.6] line-clamp-2">
                        {highlightText(article.excerpt, query.trim())}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
